import { useEffect, useState } from 'react';
import { getDashboard } from '../api/orders';
import type { DashboardData, DashboardPeriod, DashboardPoint } from '../types';

const PERIODS: { id: DashboardPeriod; label: string }[] = [
  { id: 'day', label: 'Dzień' },
  { id: 'week', label: 'Tydzień' },
  { id: 'month', label: 'Miesiąc' },
];

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [period, setPeriod] = useState<DashboardPeriod>('week');

  useEffect(() => {
    let stop = false;
    getDashboard(period)
      .then((d) => !stop && setData(d))
      .catch(() => {});
    return () => {
      stop = true;
    };
  }, [period]);

  if (!data) {
    return <div className="muted">Ładuję dashboard…</div>;
  }

  const { totals, series, sizes, colorCut, shipping, seriesUnit } = data;
  // Bars are scaled against the largest share in their own set, so the widest one fills the
  // track regardless of how concentrated the distribution is.
  const maxSizePct = Math.max(1, ...sizes.map((s) => s.pct));
  const maxColorCutPct = Math.max(1, ...colorCut.map((r) => r.pct));

  return (
    <>
      <div className="dash-stats">
        <Stat label="Zamówienia (bez anulowanych)" value={totals.orders} delta={`↑ ${totals.ordersDelta} dziś`} />
        <Stat
          label="Sprzedane koszulki"
          value={
            <>
              {totals.shirts} <span style={{ fontSize: 18, color: 'var(--admin-muted)' }}>szt</span>
            </>
          }
          delta={`↑ ${totals.shirtsDelta} w tygodniu`}
        />
        <Stat
          label="Do wysłania"
          value={<span style={{ color: 'var(--orange-deep)' }}>{totals.toShip}</span>}
          delta={`${totals.toShipOver48h} oczek. >48h`}
          deltaDown
        />
        <Stat
          label="Anulowane"
          value={totals.cancelled}
          delta={`${totals.cancelledRate.toString().replace('.', ',')}% zamówień`}
          deltaMuted
        />
      </div>

      <div className="dash-split">
        <div className="card" style={{ padding: 18 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 10,
            }}
          >
            <h2>Zamówienia · {data.periodHeading}</h2>
            <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  className={`btn-a${period === p.id ? ' primary' : ''}`}
                  style={{ padding: '4px 10px' }}
                  onClick={() => setPeriod(p.id)}
                  aria-pressed={period === p.id}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 160 }}>
            <Bars data={series} unit={seriesUnit} h={160} />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              color: 'var(--admin-muted)',
              marginTop: 6,
            }}
            className="mono"
          >
            {axisTicks(series).map((p, i, ticks) => (
              <span key={p.at}>
                {fmtTick(p.at, seriesUnit)}
                {i === ticks.length - 1 && data.countdown && ` ↑ ${data.countdown}`}
              </span>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <h2>Rozmiary koszulek</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {sizes.map((s) => (
              <div
                key={s.size}
                style={{ display: 'grid', gridTemplateColumns: '40px 1fr 90px', alignItems: 'center', gap: 10 }}
              >
                <div className="display" style={{ fontSize: 18 }}>
                  {s.size}
                </div>
                <div style={{ height: 10, background: '#EEE7D6', position: 'relative', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, (s.pct / maxSizePct) * 100)}%`,
                      background: 'var(--orange)',
                    }}
                  />
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 12,
                    color: 'var(--admin-muted)',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.count} · {s.pct}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="dash-trio">
        <div className="card" style={{ padding: 18 }}>
          <h2>Kolor / krój</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
            {colorCut.map((r) => (
              <div key={r.name}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span className="mono" style={{ color: 'var(--admin-muted)' }}>
                    {r.count} szt · {r.pct}%
                  </span>
                </div>
                <div style={{ height: 6, background: '#EEE7D6', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, (r.pct / maxColorCutPct) * 100)}%`,
                      background: r.name.startsWith('Czarna') ? 'var(--ink)' : 'var(--orange)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <h2>Wysyłka</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginTop: 8 }}>
            <ShipNum value={shipping.paczkomat} label="Paczkomat" big />
            <ShipNum value={shipping.kurier} label="Kurier" />
            <ShipNum value={shipping.odbior} label="Odbiór os." small />
            <ShipNum value={shipping.zagranica} label="Zagranica" small />
          </div>
          <div style={{ height: 8, marginTop: 14, display: 'flex' }}>
            <div style={{ flex: shipping.paczkomat.count, background: 'var(--orange)' }} />
            <div style={{ flex: shipping.kurier.count, background: '#5A4A3A' }} />
            <div style={{ flex: shipping.odbior.count, background: '#C9C2B2' }} />
            <div style={{ flex: shipping.zagranica.count, background: '#8C7A5E' }} />
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  delta,
  deltaDown,
  deltaMuted,
}: {
  label: string;
  value: import('react').ReactNode;
  delta?: string;
  deltaDown?: boolean;
  deltaMuted?: boolean;
}) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {delta && (
        <div
          className={'delta' + (deltaDown ? ' down' : '')}
          style={deltaMuted ? { color: 'var(--admin-muted)' } : undefined}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

function ShipNum({
  value,
  label,
  big,
  small,
}: {
  value: { count: number; pct: number };
  label: string;
  big?: boolean;
  small?: boolean;
}) {
  const fontSize = big ? 36 : small ? 24 : 28;
  const subFontSize = big ? 16 : small ? 12 : 14;
  return (
    <div>
      <div className="display" style={{ fontSize, color: big ? undefined : 'var(--admin-muted)' }}>
        {value.pct}
        <span style={{ fontSize: subFontSize, color: 'var(--admin-muted)' }}>%</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--admin-muted)', textTransform: 'uppercase' }}>
        {label} · {value.count}
      </div>
    </div>
  );
}

/** First, two interior and last point — deduplicated so short series don't repeat ticks. */
function axisTicks(points: DashboardPoint[]): DashboardPoint[] {
  if (points.length <= 4) return points;
  const last = points.length - 1;
  const idx = [0, Math.round(last / 3), Math.round((2 * last) / 3), last];
  return [...new Set(idx)].map((i) => points[i]!);
}

function fmtTick(iso: string, unit: 'hour' | 'day'): string {
  const d = new Date(iso);
  if (unit === 'hour') return `${String(d.getHours()).padStart(2, '0')}:00`;
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtTooltip(iso: string, unit: 'hour' | 'day'): string {
  const d = new Date(iso);
  const day = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  return unit === 'hour' ? `${day} ${String(d.getHours()).padStart(2, '0')}:00` : day;
}

function Bars({
  data,
  unit,
  h,
  color = 'var(--orange)',
}: {
  data: DashboardPoint[];
  unit: 'hour' | 'day';
  h: number;
  color?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  // A quiet period is all zeroes; without the floor every bar would be NaN-height.
  const max = Math.max(1, ...data.map((p) => p.count));
  const w = 360;
  const bw = data.length > 0 ? w / data.length : w;

  if (data.length === 0) {
    return <div className="muted" style={{ fontSize: 13 }}>Brak danych w tym okresie.</div>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="100%"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {data.map((p, i) => {
          const bh = (p.count / max) * (h - 10);
          const isHovered = hover === i;
          return (
            <g key={p.at} onMouseEnter={() => setHover(i)}>
              {/* hit target spans the full bar slot so thin gaps don't drop the hover */}
              <rect
                x={i * bw}
                y={0}
                width={bw}
                height={h}
                fill="white"
                fillOpacity={0}
                pointerEvents="all"
              />
              <rect
                x={i * bw + 2}
                y={h - bh}
                width={Math.max(1, bw - 4)}
                height={bh}
                fill={isHovered ? 'var(--orange-deep)' : color}
                rx="1"
                style={{ transition: 'fill .08s', pointerEvents: 'none' }}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && data[hover] && (
        <div
          style={{
            position: 'absolute',
            left: `${((hover * bw + bw / 2) / w) * 100}%`,
            top: -6,
            transform: 'translate(-50%, -100%)',
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '6px 10px',
            border: '1px solid var(--ink)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5,
            textAlign: 'center',
          }}
        >
          <div
            className="display"
            style={{ fontSize: 18, color: 'var(--orange)', lineHeight: 1 }}
          >
            {data[hover]!.count}
          </div>
          <div className="mono" style={{ fontSize: 10, marginTop: 2, opacity: 0.7, letterSpacing: '.08em' }}>
            {fmtTooltip(data[hover]!.at, unit)} · ZAMÓWIEŃ
          </div>
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: -5,
              transform: 'translateX(-50%) rotate(45deg)',
              width: 8,
              height: 8,
              background: 'var(--ink)',
            }}
          />
        </div>
      )}
    </div>
  );
}
